import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  ContentComparisonCreatePayload,
  ContentComparisonCreateResponse,
  ContentComparisonListResponse,
  ContentComparisonResponse,
} from './interfaces/contentComparison.interface'

// Content Analysis → Comparison: several analyses over one window.
// Account-scoped, not under an analysis — the report belongs to none of them,
// and the server checks every id against the caller's own analyses.
const BASE = '/v1_private/content-comparisons'

// `attachment; filename="…"` -> the filename. Readable cross-origin because the
// endpoint sends Access-Control-Expose-Headers: Content-Disposition.
const readFilename = (response: { headers: Record<string, unknown> }) => {
  const header = response.headers['content-disposition']

  if (typeof header !== 'string') {
    return undefined
  }

  return header.match(/filename="?([^";]+)"?/)?.[1]
}

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

class ContentComparisonApiService {
  private static _instance: ContentComparisonApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): ContentComparisonApiService {
    if (!ContentComparisonApiService._instance) {
      ContentComparisonApiService._instance = new ContentComparisonApiService()
    }

    return ContentComparisonApiService._instance
  }

  // Headers are passed per call as well as on the instance: the session id is
  // read at call time, so a login after the singleton was built still sends the
  // current one. Mirrors CampaignComparisonApiService.
  public static async getList(): Promise<ContentComparisonListResponse> {
    const { data } =
      await ContentComparisonApiService.instance.client.get<ContentComparisonListResponse>(
        BASE,
        { headers: getHeaders() }
      )

    return data
  }

  public static async getOne(
    comparisonId: string
  ): Promise<ContentComparisonResponse> {
    const { data } =
      await ContentComparisonApiService.instance.client.get<ContentComparisonResponse>(
        `${BASE}/${comparisonId}`,
        { headers: getHeaders() }
      )

    return data
  }

  public static async create(
    payload: ContentComparisonCreatePayload
  ): Promise<ContentComparisonCreateResponse> {
    const { data } =
      await ContentComparisonApiService.instance.client.post<ContentComparisonCreateResponse>(
        BASE,
        payload,
        { headers: getHeaders() }
      )

    return data
  }

  public static async remove(comparisonId: string): Promise<void> {
    await ContentComparisonApiService.instance.client.delete(
      `${BASE}/${comparisonId}`,
      { headers: getHeaders() }
    )
  }

  // Fetched as a blob and saved through an object URL rather than navigating to
  // the endpoint: it needs the session headers, which a plain window.open or
  // <a href> cannot send. The deck is rendered on demand, so this request takes
  // as long as a render — the caller shows the row's downloading state until it
  // resolves.
  public static async download(comparisonId: string, fallbackFilename: string) {
    const response = await ContentComparisonApiService.instance.client.get(
      `${BASE}/${comparisonId}/download`,
      { headers: getHeaders(), responseType: 'blob' }
    )

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    // The server names the file after the analyses and the window, so that name
    // is preferred over anything reconstructed here; the fallback only covers a
    // response whose header did not survive.
    link.download = readFilename(response) || fallbackFilename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }
}

export { ContentComparisonApiService }
